import { useState, useEffect } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { AppRoutes } from './router'
import { RoleProvider, useRole } from './contexts/RoleContext'
import { ThemeProvider } from './contexts/ThemeContext'
import BottomNavigation from './components/feature/BottomNavigation'
import SplashScreen from './components/feature/SplashScreen'
import RoleSelectionScreen from './components/feature/RoleSelectionScreen'
import DriverLogin from './components/feature/DriverLogin'
import { ToastHost, useToast } from './components/feature/appkit'
import { useInvite, hasPendingInvite, dropPendingInvite } from './hooks/useInvite'

type AppPhase = 'splash' | 'login' | 'app'

/**
 * Compte interne connecté à l'app : on le dit, on ne le laisse pas errer.
 *
 * Avant, « ceo_admin » était assimilé à « fleet_manager » : le compte
 * atterrissait sur la partie gestionnaire, sans société rattachée, devant des
 * écrans vides. Le rôle interne pilote la console, pas l'app.
 */
function InternalAccountNotice({ email, onLogout }: { email: string; onLogout: () => void }) {
  return (
    <div
      style={{
        position: 'absolute', inset: 0, background: 'var(--ink)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 18, padding: 28, textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 58, height: 58, borderRadius: 17, background: 'var(--ink-2)',
          border: '1px solid var(--ink-line)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <i className="ri-shield-star-line" style={{ fontSize: 27, color: '#f87171' }}></i>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 320 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.11em', textTransform: 'uppercase', color: 'var(--ink-t)' }}>
          Compte interne
        </span>
        <span style={{ fontSize: 22, fontWeight: 700, color: '#fff', letterSpacing: '-.02em', lineHeight: 1.2 }}>
          Cette app n'est pas la vôtre
        </span>
        <span style={{ fontSize: 13.5, color: 'var(--ink-t)', lineHeight: 1.55 }}>
          {email} est un compte d'administration iAUTOLINK. Il ne conduit pas et ne gère
          aucune société : sa console est ailleurs.
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 300 }}>
        <a
          href="https://admin.autolink.lu"
          style={{
            minHeight: 54, borderRadius: 14, background: 'var(--brand)', color: '#fff',
            fontSize: 15.5, fontWeight: 700, display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 9, textDecoration: 'none',
          }}
        >
          <i className="ri-external-link-line" style={{ fontSize: 19 }}></i>
          Ouvrir la console interne
        </a>
        <button
          onClick={onLogout}
          style={{
            minHeight: 50, border: '1px solid var(--ink-line)', borderRadius: 13,
            background: 'var(--ink-2)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Se déconnecter
        </button>
      </div>
    </div>
  )
}

/**
 * Arrivée par un lien d'invitation, sans être connecté.
 *
 * Sans cet écran, le lien tombait sur le sélecteur de rôle générique : rien
 * n'indiquait qu'une invitation était en cours, et un conducteur invité
 * pouvait parfaitement cliquer sur « Gestionnaire de flotte ».
 *
 * Il répond aussi à la question que tout le monde se pose devant un lien
 * d'invitation : « et mon mot de passe ? ». Il n'y en a pas d'attribué — le
 * lien ne crée pas de compte, il rattache un compte à une société. Le
 * conducteur choisit donc son propre mot de passe à l'inscription.
 */
function InviteWelcome({ onContinue, onDismiss }: { onContinue: () => void; onDismiss: () => void }) {
  return (
    <div
      style={{
        position: 'absolute', inset: 0, background: 'var(--ink)',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        gap: 22, padding: 'calc(var(--safe-t) + 28px) 24px calc(var(--safe-b) + 28px)',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
        <div
          style={{
            width: 58, height: 58, borderRadius: 17, background: 'var(--ink-2)',
            border: '1px solid var(--ink-line)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <i className="ri-mail-check-line" style={{ fontSize: 27, color: '#34d399' }}></i>
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.11em', textTransform: 'uppercase', color: 'var(--ink-t)' }}>
          Invitation · conducteur
        </span>
        <span style={{ fontSize: 25, fontWeight: 700, color: '#fff', letterSpacing: '-.02em', lineHeight: 1.18 }}>
          Votre société vous a invité
        </span>
        <span style={{ fontSize: 13.5, color: 'var(--ink-t)', lineHeight: 1.55 }}>
          Créez votre compte conducteur : vous choisissez vous-même votre mot de passe.
          Le rattachement à votre société se fait ensuite tout seul.
        </span>
      </div>

      <div
        style={{
          border: '1px solid var(--ink-line)', borderRadius: 15, background: 'var(--ink-2)',
          padding: 16, display: 'flex', gap: 11,
        }}
      >
        <i className="ri-shield-keyhole-line" style={{ fontSize: 17, color: 'var(--ink-t)', flex: 'none', marginTop: 1 }}></i>
        <span style={{ flex: 1, fontSize: 12, color: 'var(--ink-t)', lineHeight: 1.5 }}>
          Ce lien ne contient aucun mot de passe et n'en attribue aucun. Utilisez
          l'adresse e-mail à laquelle votre gestionnaire vous a écrit : le
          rattachement est refusé pour toute autre adresse.
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button
          onClick={onContinue}
          style={{
            minHeight: 56, border: 'none', borderRadius: 14, background: 'var(--brand)',
            color: '#fff', fontSize: 15.5, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
          }}
        >
          <i className="ri-user-add-line" style={{ fontSize: 19 }}></i>
          Créer mon compte conducteur
        </button>
        <button
          onClick={onDismiss}
          style={{
            minHeight: 48, border: 'none', background: 'transparent',
            color: 'var(--ink-t)', fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Ce n'est pas moi — ouvrir l'accueil
        </button>
      </div>
    </div>
  )
}

function AppContent() {
  const { role, authLoading, isInternal, email, logout, profile } = useRole()
  const toast = useToast()

  // Rattachement par lien d'invitation : `?invite=<token>` est consommé dès
  // que l'utilisateur est connecté et sans société.
  const invite = useInvite(role !== null, !!profile?.company_id)
  useEffect(() => {
    if (invite.status === 'joined') toast('Vous êtes rattaché à votre société', 'ok')
    if (invite.status === 'failed') toast(invite.message, 'err')
  }, [invite, toast])

  const [phase, setPhase] = useState<AppPhase>(() => {
    if (!sessionStorage.getItem('splash_shown')) return 'splash'
    return 'login'
  })

  // Étape d'accueil spécifique quand on arrive par un lien d'invitation.
  const [inviteStep, setInviteStep] = useState<'none' | 'welcome' | 'signup'>(
    () => (hasPendingInvite() ? 'welcome' : 'none'),
  )

  // Quand l'authentification est résolue : connecté → app, sinon → connexion.
  useEffect(() => {
    if (authLoading) return
    setPhase(role !== null || isInternal ? 'app' : 'login')
  }, [role, isInternal, authLoading])

  const handleSplashDone = () => {
    sessionStorage.setItem('splash_shown', 'true')
    setPhase(role !== null || isInternal ? 'app' : 'login')
  }

  // Ne pas démonter l'app déjà affichée si l'authentification repasse en
  // chargement : c'est ce démontage/remontage qui produisait un loader
  // clignotant en boucle.
  if (authLoading && phase !== 'app') {
    return (
      <div
        style={{
          position: 'absolute', inset: 0, background: 'var(--bg)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <i className="ri-loader-4-line" style={{ fontSize: 24, color: 'var(--brand)', animation: 'alSpin 1s linear infinite' }}></i>
          <span style={{ fontSize: 11.5, color: 'var(--faint)', fontWeight: 600 }}>AutoLink</span>
        </div>
      </div>
    )
  }

  return (
    <>
      {phase === 'splash' && <SplashScreen onDone={handleSplashDone} />}
      {phase === 'login' && inviteStep === 'welcome' && (
        <InviteWelcome
          onContinue={() => setInviteStep('signup')}
          onDismiss={() => { dropPendingInvite(); setInviteStep('none') }}
        />
      )}
      {phase === 'login' && inviteStep === 'signup' && (
        <DriverLogin onBack={() => setInviteStep('welcome')} />
      )}
      {phase === 'login' && inviteStep === 'none' && <RoleSelectionScreen />}
      {phase === 'app' && isInternal && (
        <InternalAccountNotice email={email} onLogout={() => logout()} />
      )}
      {phase === 'app' && !isInternal && (
        /* Les écrans du design v2 sont en position absolue et défilent
           chacun dans leur propre colonne (SCROLL) : le conteneur ne doit
           PAS défiler, sinon le bandeau d'encre se décolle du haut. */
        <div style={{ position: 'absolute', inset: 0, background: 'var(--bg)', overflow: 'hidden' }}>
          <AppRoutes />
          <BottomNavigation />
        </div>
      )}
    </>
  )
}

function App() {
  return (
    <BrowserRouter basename={__BASE_PATH__}>
      <ThemeProvider>
        <RoleProvider>
          <div
            className="min-h-screen w-full flex justify-center"
            style={{ background: 'var(--sk)' }}
          >
            <div
              className="relative w-full overflow-hidden"
              style={{ maxWidth: '520px', height: '100dvh', background: 'var(--bg)' }}
            >
              <ToastHost>
                <AppContent />
              </ToastHost>
            </div>
          </div>
        </RoleProvider>
      </ThemeProvider>
    </BrowserRouter>
  )
}

export default App
